import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// @ts-ignore
import WifiOff from 'lucide-react/dist/esm/icons/wifi-off';
import { loginConferente, isConferenteLoggedIn, getConferenteCnpjSlug } from '../../services/conferente';
import { getUser as getMainUser, isAuthenticated as isMainAuthenticated } from '../../services/config';
import { sincronizarConfiguracoes, sincronizarMontagensCargas, sincronizarConferencias } from '../../services/offlineData';
import PWAInstallPrompt from '../../components/PWA/PWAInstallPrompt';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import '../../components/Auth/Login.css';

// =============================================
// CONTAS SALVAS (localStorage)
// =============================================
const SAVED_ACCOUNTS_KEY = 'conferente_saved_accounts';

interface SavedAccount {
  email: string;
  senha: string; // base64
  nome: string;
}

function getSavedAccounts(): SavedAccount[] {
  try {
    const raw = localStorage.getItem(SAVED_ACCOUNTS_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

// Codificacao segura para Unicode (btoa nao suporta caracteres > Latin1)
function encodePassword(senha: string): string {
  return btoa(encodeURIComponent(senha));
}

function decodePassword(encoded: string): string {
  return decodeURIComponent(atob(encoded));
}

function saveAccount(email: string, senha: string, nome: string) {
  const accounts = getSavedAccounts().filter(a => a.email !== email);
  accounts.unshift({ email, senha: encodePassword(senha), nome });
  localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(accounts));
}

function removeAccount(email: string) {
  const accounts = getSavedAccounts().filter(a => a.email !== email);
  localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(accounts));
}

// =============================================
// COMPONENTE
// =============================================
const ConferenteLogin: React.FC = () => {
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [syncStatus, setSyncStatus] = useState('');
  const [lembrar, setLembrar] = useState(true);
  const [savedAccounts, setSavedAccounts] = useState<SavedAccount[]>([]);
  const [showSavedAccounts, setShowSavedAccounts] = useState(true);
  const [cnpj, setCnpj] = useState(() => {
    const saved = getConferenteCnpjSlug();
    if (!saved) return '';
    // Formatar o CNPJ salvo (somente dígitos) para exibição
    return saved
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
      .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5');
  });
  const [cnpjError, setCnpjError] = useState('');

  useEffect(() => {
    if (isConferenteLoggedIn()) {
      navigate('/conferente', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    setSavedAccounts(getSavedAccounts());
  }, []);

  const loadingRef = React.useRef(false);

  const doLogin = useCallback(async (loginEmail: string, loginSenha: string, salvarConta: boolean) => {
    if (loadingRef.current) return;
    if (!loginEmail.trim() || !loginSenha.trim()) return;

    if (!navigator.onLine) {
      setError('Sem conexão com a internet. O login requer conexão. Se você já fez login antes, tente reabrir o aplicativo.');
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    setError('');

    try {
      const dispositivo_info = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        standalone: (window.navigator as any).standalone || window.matchMedia('(display-mode: standalone)').matches
      };

      const data = await loginConferente(loginEmail, loginSenha, dispositivo_info);

      if (salvarConta) {
        const nome = data.usuario?.nome || loginEmail.split('@')[0];
        saveAccount(loginEmail, loginSenha, nome);
      }

      setSyncStatus('Preparando dados offline...');
      try {
        await sincronizarConfiguracoes();
        setSyncStatus('Sincronizando montagens de cargas...');
        await sincronizarMontagensCargas();
        setSyncStatus('Sincronizando conferencias...');
        await sincronizarConferencias();
      } catch {
        // Continuar mesmo com erro
      }

      navigate('/conferente', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Erro ao fazer login');
      // Se falhou, mostra o formulario para o usuario poder corrigir
      setShowSavedAccounts(false);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [navigate]);

  const selectAccount = useCallback((account: SavedAccount) => {
    let decodedSenha = '';
    try {
      decodedSenha = decodePassword(account.senha);
    } catch {
      try {
        // Fallback para contas salvas antes da correcao Unicode
        decodedSenha = atob(account.senha);
      } catch {
        decodedSenha = '';
      }
    }

    // Apenas o email vai para o state - a senha NUNCA fica no state/DOM
    // para evitar exposicao via inspecionar elementos
    setEmail(account.email);
    setSenha('');

    if (decodedSenha) {
      // Login automatico ao selecionar conta salva (sempre salva pois ja era conta salva)
      doLogin(account.email, decodedSenha, true);
    } else {
      setShowSavedAccounts(false);
    }
  }, [doLogin]);

  const handleRemoveAccount = useCallback((e: React.MouseEvent, emailToRemove: string) => {
    e.stopPropagation();
    removeAccount(emailToRemove);
    setSavedAccounts(getSavedAccounts());
    setEmail((prev: string) => {
      if (prev === emailToRemove) {
        setSenha('');
        return '';
      }
      return prev;
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await doLogin(email, senha, lembrar);
  };

  const getInitials = (nome: string) => {
    return nome.split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  };

  const formatCnpj = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    return digits
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
      .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5');
  };

  const handleCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCnpjError('');
    setCnpj(formatCnpj(e.target.value));
  };

  const handleCnpjSubmit = () => {
    const digits = cnpj.replace(/\D/g, '');
    if (digits.length !== 14) {
      setCnpjError('Digite um CNPJ válido com 14 dígitos');
      return;
    }
    navigate(`/conferente/${digits}`);
  };

  // Verificar se o usuário está logado no dashboard principal como admin/supervisor
  const mainUser = isMainAuthenticated() ? getMainUser() : null;
  const isMainAdmin = mainUser && (mainUser.tipo === 'admin' || mainUser.tipo === 'supervisor');

  return (
    <div className="login-page" style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 50%, #14532d 100%)' }}>
      <PWAInstallPrompt />
      {isMainAdmin && (
        <a
          href="/"
          style={{
            position: 'fixed',
            top: '16px',
            right: '16px',
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            color: '#15803d',
            fontWeight: 600,
            fontSize: '0.85rem',
            textDecoration: 'none',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)'; }}
          title="Voltar ao Painel Administrativo"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
          Painel Admin
        </a>
      )}
      <div className="login-container">
        <div className="logo-section">
          <img
            className="logo"
            src="/images/logo-confereai.png"
            alt="Logo ConfereAí"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
          <p className="logo-subtitle">Conferência de Cargas</p>
        </div>

        {!isOnline && (
          <div style={{ background: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.3)', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <WifiOff style={{ width: '16px', height: '16px', color: '#ca8a04', flexShrink: 0 }} />
            <p style={{ fontSize: '0.85rem', color: '#a16207', margin: 0 }}>Sem conexão. O login requer internet.</p>
          </div>
        )}

        {error && (
          <div className="error-message" style={{ display: 'block' }}>
            {error}
          </div>
        )}

        {/* Lista de contas salvas */}
        {savedAccounts.length > 0 && showSavedAccounts && (
          <div style={{ marginBottom: '20px', opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '10px', fontWeight: 500 }}>
              Contas salvas neste dispositivo:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {savedAccounts.map((account) => (
                <div
                  key={account.email}
                  onClick={() => selectAccount(account)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 14px',
                    background: email === account.email && loading ? 'rgba(22, 163, 74, 0.15)' : 'rgba(22, 163, 74, 0.06)',
                    border: email === account.email && loading ? '1.5px solid rgba(22, 163, 74, 0.5)' : '1.5px solid rgba(22, 163, 74, 0.2)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (loading) return;
                    e.currentTarget.style.background = 'rgba(22, 163, 74, 0.12)';
                    e.currentTarget.style.borderColor = 'rgba(22, 163, 74, 0.4)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    if (loading) return;
                    e.currentTarget.style.background = 'rgba(22, 163, 74, 0.06)';
                    e.currentTarget.style.borderColor = 'rgba(22, 163, 74, 0.2)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Avatar com iniciais */}
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #16a34a, #15803d)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    flexShrink: 0,
                  }}>
                    {getInitials(account.nome)}
                  </div>
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {account.nome}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {account.email}
                    </div>
                  </div>
                  {/* Spinner na conta selecionada durante loading */}
                  {email === account.email && loading ? (
                    <span className="loading-spinner" style={{ width: '18px', height: '18px', borderColor: 'rgba(22, 163, 74, 0.2)', borderTopColor: '#16a34a', flexShrink: 0 }} />
                  ) : (
                    /* Botao remover */
                    <button
                      type="button"
                      onClick={(e) => handleRemoveAccount(e, account.email)}
                      title="Remover conta salva"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        fontSize: '1.1rem',
                        color: '#94a3b8',
                        lineHeight: 1,
                        flexShrink: 0,
                        borderRadius: '6px',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#ef4444';
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#94a3b8';
                        e.currentTarget.style.background = 'none';
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            {!loading && (
              <button
                type="button"
                onClick={() => setShowSavedAccounts(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#64748b',
                  fontSize: '0.8rem',
                  marginTop: '8px',
                  padding: '4px 0',
                  textDecoration: 'underline',
                }}
              >
                Usar outro e-mail
              </button>
            )}
            {loading && (
              <p style={{ fontSize: '0.8rem', color: '#16a34a', marginTop: '10px', fontWeight: 500 }}>
                {syncStatus || 'Entrando...'}
              </p>
            )}
          </div>
        )}

        <form id="loginForm" onSubmit={handleSubmit} autoComplete="off">
          {/* Mostrar campos de email/senha quando nao ha contas salvas ou usuario quer digitar manualmente */}
          {(!savedAccounts.length || !showSavedAccounts) && (
            <>
              <div className="form-group">
                <label htmlFor="email">E-mail</label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Digite seu e-mail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="off"
                  />
                  <span className="input-icon">📧</span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="senha">Senha</label>
                <div className="input-wrapper">
                  <input
                    type="password"
                    id="senha"
                    name="senha"
                    placeholder="Digite sua senha"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    required
                    autoComplete="off"
                  />
                  <span className="input-icon">🔒</span>
                </div>
              </div>

              {/* Checkbox lembrar dados */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                <input
                  type="checkbox"
                  id="lembrar"
                  checked={lembrar}
                  onChange={(e) => setLembrar(e.target.checked)}
                  style={{
                    width: '18px',
                    height: '18px',
                    accentColor: '#16a34a',
                    cursor: 'pointer',
                  }}
                />
                <label
                  htmlFor="lembrar"
                  style={{
                    fontSize: '0.9rem',
                    color: '#475569',
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontWeight: 400,
                    textTransform: 'none',
                    letterSpacing: 'normal',
                  }}
                >
                  Lembrar meus dados neste dispositivo
                </label>
              </div>

              {savedAccounts.length > 0 && !showSavedAccounts && (
                <button
                  type="button"
                  onClick={() => { setShowSavedAccounts(true); setEmail(''); setSenha(''); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#16a34a',
                    fontSize: '0.85rem',
                    marginBottom: '16px',
                    padding: '4px 0',
                    fontWeight: 500,
                    textDecoration: 'underline',
                  }}
                >
                  Voltar para contas salvas
                </button>
              )}
            </>
          )}

          {(!savedAccounts.length || !showSavedAccounts) && (
            <button
              type="submit"
              className="login-btn"
              id="loginBtn"
              disabled={loading || !email.trim() || !senha.trim() || !isOnline}
            >
              {loading ? (
                <>
                  <span className="loading-spinner" />
                  <span id="btnText">{syncStatus || 'Entrando...'}</span>
                </>
              ) : (
                <span id="btnText">Entrar</span>
              )}
            </button>
          )}
        </form>

        <div style={{ textAlign: 'center', marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
          <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 8px 0', fontWeight: 500 }}>
            Conferente diarista?
          </p>
          <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: '0 0 12px 0' }}>
            Digite o CNPJ da transportadora para entrar com CPF
          </p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <input
                type="text"
                inputMode="numeric"
                placeholder="00.000.000/0000-00"
                value={cnpj}
                onChange={handleCnpjChange}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCnpjSubmit(); }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: cnpjError ? '1.5px solid #ef4444' : '1.5px solid #e2e8f0',
                  fontSize: '0.9rem',
                  outline: 'none',
                  textAlign: 'center',
                  letterSpacing: '0.5px',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => { if (!cnpjError) e.currentTarget.style.borderColor = '#16a34a'; }}
                onBlur={(e) => { if (!cnpjError) e.currentTarget.style.borderColor = '#e2e8f0'; }}
              />
              {cnpjError && (
                <p style={{ fontSize: '0.75rem', color: '#ef4444', margin: '4px 0 0 0', textAlign: 'left' }}>{cnpjError}</p>
              )}
            </div>
            <button
              type="button"
              onClick={handleCnpjSubmit}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: 'none',
                background: 'linear-gradient(135deg, #16a34a, #15803d)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              Entrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConferenteLogin;
